import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Button } from './ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { MessageCircle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MessageButton - Opens a modal to start a conversation with a user
 * 
 * @param {string} targetUserId - The user_id to message
 * @param {string} targetName - Display name of the user
 * @param {string} variant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (default, sm, lg, icon)
 * @param {boolean} showText - Show "Message" text or just icon
 */
export function MessageButton({ 
  targetUserId, 
  targetName = 'this user',
  variant = 'outline',
  size = 'default',
  showText = true,
  className = ''
}) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleClick = () => {
    if (!user || !token) {
      toast.error('Please sign in to send messages');
      navigate('/login');
      return;
    }
    
    if (targetUserId === user.user_id) {
      toast.error("You can't message yourself");
      return;
    }
    
    setShowModal(true);
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/messages/conversations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_user_id: targetUserId,
          initial_message: message.trim()
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Message sent!');
        setShowModal(false);
        setMessage('');
        // Navigate to the conversation
        navigate(`/messages/${data.conversation_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        className={className}
        data-testid="message-button"
      >
        <MessageCircle className={`w-4 h-4 ${showText ? 'mr-2' : ''}`} />
        {showText && 'Message'}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Message {targetName}</DialogTitle>
            <DialogDescription>
              Start a conversation with {targetName}. They'll receive your message in their inbox or message requests.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Say hi to ${targetName}...`}
              className="min-h-[100px] resize-none"
              maxLength={1000}
              disabled={sending}
              data-testid="message-textarea"
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/1000
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              data-testid="send-initial-message-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MessageButton;
